import React from "react";
import { Check, ChevronDown, Lock } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { MODEL_FAMILIES } from "../constants/models";
import { useUpgrade } from "../context/UpgradeContext";

export function ModelDot({ family, className = "h-2.5 w-2.5" }) {
  const fam = MODEL_FAMILIES[family] || MODEL_FAMILIES.openai;
  return <span className={`rounded-full ${className}`} style={{ background: fam.color }} />;
}

export function ModelBadge({ family, label }) {
  const fam = MODEL_FAMILIES[family] || MODEL_FAMILIES.openai;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold border"
      style={{ background: fam.badgeBg, color: fam.badgeText, borderColor: fam.border }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: fam.color }} />
      {label}
    </span>
  );
}

export default function ModelSwitcher({ models, value, onChange }) {
  const { isPro, openUpgrade } = useUpgrade();
  const current = models.find((m) => m.key === value) || models[0];
  if (!current) return null;

  const grouped = models.reduce((acc, m) => {
    (acc[m.family] = acc[m.family] || []).push(m);
    return acc;
  }, {});

  const handleSelect = (m) => {
    if (!isPro && m.tier === "pro") {
      openUpgrade(`${m.label} is a Pro model. Upgrade to chat with every frontier model.`);
      return;
    }
    onChange(m.key);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="model-switcher-trigger"
          className="flex items-center gap-2 rounded-full border border-[#E6E6DF] bg-white px-3.5 py-2 hover:border-[#CECEBE] focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/40"
          style={{ transition: "border-color 0.2s ease" }}
        >
          <ModelDot family={current.family} />
          <span className="text-sm font-semibold text-[#171716]">{current.label}</span>
          <ChevronDown className="h-4 w-4 text-[#8C8C85]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-h-[70vh] overflow-y-auto omni-scroll" data-testid="model-switcher-menu">
        {Object.entries(grouped).map(([family, list], gi) => (
          <div key={family}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[#8C8C85]">
              {(MODEL_FAMILIES[family] || {}).label || family}
            </DropdownMenuLabel>
            {list.map((m) => {
              const locked = !isPro && m.tier === "pro";
              return (
                <DropdownMenuItem
                  key={m.key}
                  data-testid={`model-option-${m.key}`}
                  onSelect={(e) => { e.preventDefault(); handleSelect(m); }}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <ModelDot family={m.family} className="h-2 w-2" />
                    <span className={`text-sm ${locked ? "text-[#8C8C85]" : ""}`}>{m.label}</span>
                  </span>
                  {m.key === value ? (
                    <Check className="h-4 w-4 text-[#2D6A4F]" />
                  ) : locked ? (
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-[#8C8C85]">
                      <Lock className="h-3 w-3" /> PRO
                    </span>
                  ) : m.tier === "free" ? (
                    <span className="text-[10px] font-semibold text-[#2D6A4F]">FREE</span>
                  ) : null}
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
